/**
 * src/components/teacher-dashboard-components/theme/components/InfoCard.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';
import Card from './Card';
import Input from './Input';
import ToggleSwitch from './ToggleSwitch';

const InfoCardHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.$bgColor || getColor('primary.blue')};
  color: ${props => props.$textColor || getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.lg} ${props => props.theme.borderRadius.lg} 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
    border-radius: ${props.theme.borderRadius.md} ${props.theme.borderRadius.md} 0 0;
  `}
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const IconContainer = styled.div`
  padding: ${props => props.theme.spacing.sm};
  background: ${getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h4.fontSize : 
    props.theme.typography.desktop.h4.fontSize};
  font-weight: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h4.fontWeight : 
    props.theme.typography.desktop.h4.fontWeight};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${getColor('neutral.white')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  ${createTransition('background', 'fast')};
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoCardContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  
  ${props => !props.theme.isMobile && `
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`;

const FieldLabel = styled.label`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.small.fontSize : 
    props.theme.typography.desktop.small.fontSize};
  font-weight: 500;
  color: ${getColor('neutral.dark')};
`;

const FieldValue = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.body.fontSize : 
    props.theme.typography.desktop.body.fontSize};
  font-weight: 400;
  color: ${getColor('neutral.black')};
  min-height: 1.5rem;
  display: flex;
  align-items: center;
`;

const ErrorMessage = styled.div`
  color: ${getColor('semantic.error')};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.caption.fontSize : 
    props.theme.typography.desktop.caption.fontSize};
  margin-top: ${props => props.theme.spacing.xs};
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${getColor('neutral.medium')};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$active ? props.$bgColor : getColor('neutral.white')};
  color: ${props => props.$active ? props.$textColor : getColor('neutral.black')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  ${createTransition('all', 'fast')};
  
  &:hover {
    background: ${props => props.$active ? props.$bgColor : getColor('neutral.light')};
  }
`;

/**
 * Info Card.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const InfoCard = React.forwardRef(({
  icon: Icon,
  title,
  bgColor,
  textColor,
  iconColor,
  fields = [],
  children,
  onSave,
  showEdit = true,
  className,
  ...props
}, ref) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
    /**
   * Handle Start Edit.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleStartEdit = () => {
    const initialEdits = {};
    fields.forEach(field => {
      if (field.editable !== false) {
        initialEdits[field.label] = field.value || '';
      }
    });
    setEditedFields(initialEdits);
    setIsEditing(true);
    setError(null);
  };
  
    /**
   * Handle Save.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await onSave?.(editedFields);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
    /**
   * Handle Cancel.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleCancel = () => {
    setIsEditing(false);
    setEditedFields({});
    setError(null);
  };
  
    /**
   * Render Field Value.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const renderFieldValue = (field) => {
    if (field.value == null) return '';
    
    if (typeof field.value === 'object' && !Array.isArray(field.value)) {
      if ('street' in field.value) return field.value.street;
      if ('city' in field.value) return field.value.city;
      if ('postalCode' in field.value) return field.value.postalCode;
      if ('country' in field.value) return field.value.country;
    }
    
    return field.value.toString();
  };
  
    /**
   * Render Field Input.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const renderFieldInput = (field) => {
    if (field.type === 'toggle') {
      return (
        <ToggleSwitch
          checked={editedFields[field.label] || false}
          onChange={(checked) => setEditedFields({
            ...editedFields,
            [field.label]: checked
          })}
        />
      );
    }
    
    if (field.type === 'select' && field.options) {
      return (
        <select
          value={editedFields[field.label] || ''}
          onChange={(e) => setEditedFields({
            ...editedFields,
            [field.label]: e.target.value
          })}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: `1px solid ${getColor('neutral.medium')}`,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.isMobile ? 
              theme.typography.mobile.body.fontSize : 
              theme.typography.desktop.body.fontSize
          }}
        >
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    
    return (
      <Input
        type={field.type || 'text'}
        value={editedFields[field.label] || ''}
        onChange={(e) => setEditedFields({
          ...editedFields,
          [field.label]: e.target.value
        })}
        placeholder={field.placeholder}
      />
    );
  };
  
  return (
    <Card ref={ref} variant="elevated" padding="none" className={className} {...props}>
      <InfoCardHeader theme={theme} $bgColor={bgColor} $textColor={textColor}>
        <HeaderLeft theme={theme}>
          {Icon && (
            <IconContainer theme={theme}>
              <Icon 
                size={theme.isMobile ? 16 : 20} 
                color={iconColor || getColor('primary.blue')} 
              />
            </IconContainer>
          )}
          <HeaderTitle theme={theme}>{title}</HeaderTitle>
        </HeaderLeft>
        
        {showEdit && !isEditing && fields.length > 0 && (
          <ActionButtons theme={theme}>
            <ActionButton theme={theme} onClick={handleStartEdit} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
            </ActionButton>
          </ActionButtons>
        )}
        
        {showEdit && isEditing && (
          <ActionButtons theme={theme}>
            <ActionButton theme={theme} onClick={handleSave} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </ActionButton>
            <ActionButton theme={theme} onClick={handleCancel} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </ActionButton>
          </ActionButtons>
        )}
      </InfoCardHeader>
      
      <InfoCardContent theme={theme}>
        <FieldContainer theme={theme}>
          {fields.map((field, index) => (
            <FieldRow key={index} theme={theme}>
              <FieldLabel theme={theme}>{field.label}</FieldLabel>
              <FieldValue theme={theme}>
                {isEditing && field.editable !== false ? (
                  renderFieldInput(field)
                ) : field.type === 'toggle' ? (
                  <ToggleSwitch
                    checked={field.value}
                    onChange={() => {}}
                    disabled
                  />
                ) : (
                  renderFieldValue(field)
                )}
              </FieldValue>
              
              {field.dropdown && !isEditing && (
                <DropdownContainer theme={theme}>
                  {field.dropdown.options.map((option) => (
                    <DropdownOption
                      key={option.code}
                      theme={theme}
                      $active={option.active}
                      $bgColor={bgColor}
                      $textColor={textColor}
                      onClick={() => field.dropdown.onChange(option.code, option.name)}
                    >
                      {option.flag && (
                        <img
                          src={option.flag}
                          alt={option.name}
                          style={{ width: '20px', height: '15px', objectFit: 'cover' }}
                        />
                      )}
                      <span>{option.name}</span>
                      {option.active && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                      )}
                    </DropdownOption>
                  ))}
                </DropdownContainer>
              )}
            </FieldRow>
          ))}
        </FieldContainer>
        
        {error && (
          <ErrorMessage theme={theme}>
            {error}
          </ErrorMessage>
        )}
        
        {children}
      </InfoCardContent>
    </Card>
  );
});

export default InfoCard;
